import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { safeFormat } from '@/lib/safeDate';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowDown, ArrowUp, TrendingUp, TrendingDown } from 'lucide-react';
import { MoneyDisplay } from '../MoneyDisplay';
import { useCashFlowRealized } from '@/hooks/finance/useCashFlowData';
import type { BusinessUnit } from '../BusinessUnitSelector';
import type { Brand } from '../BrandSelector';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDate } from '@/lib/localizedDateUtils';

interface CashFlowRealizedProps {
  businessUnit?: BusinessUnit;
  brand?: Brand;
  days?: number;
}

export function CashFlowRealized({ 
  businessUnit = 'all', 
  brand = 'all',
  days = 30 
}: CashFlowRealizedProps) {
  const { data, isLoading } = useCashFlowRealized(businessUnit, brand, days);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const variacao = data?.variacao || 0;
  const variacaoPercent = data?.saldoInicial 
    ? ((variacao / data.saldoInicial) * 100).toFixed(0) 
    : '0';

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-muted-foreground">Saldo Inicial ({safeFormat(new Date(), 'dd/MM', { locale: ptBR })})</span>
            <MoneyDisplay value={data?.saldoInicial || 0} showCents={false} className="font-semibold" />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center text-emerald-600">
              <span className="flex items-center gap-2">
                <ArrowDown className="h-4 w-4" />
                (+) Total Entradas
              </span>
              <MoneyDisplay value={data?.totalEntradas || 0} showCents={false} />
            </div>
            <div className="pl-6 text-sm text-muted-foreground">
              └ Recebimentos de clientes: <MoneyDisplay value={data?.totalEntradas || 0} showCents={false} className="inline" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center text-destructive">
              <span className="flex items-center gap-2">
                <ArrowUp className="h-4 w-4" />
                (-) Total Saídas
              </span>
              <MoneyDisplay value={data?.totalSaidas || 0} showCents={false} />
            </div>
          </div>

          <div className="flex justify-between items-center py-2 border-t border-b font-bold">
            <span>= Saldo Final (Atual)</span>
            <MoneyDisplay value={data?.saldoFinal || 0} showCents={false} />
          </div>

          <div className={`flex justify-between items-center ${variacao >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
            <span className="flex items-center gap-2">
              {variacao >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              Variação
            </span>
            <span>
              {variacao >= 0 ? '+' : ''}<MoneyDisplay value={variacao} showCents={false} className="inline" /> 
              ({variacao >= 0 ? '+' : ''}{variacaoPercent}%) 
              {variacao >= 0 ? '✅' : '❌'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Movimentações */}
      <Card>
        <CardHeader>
          <CardTitle>Movimentações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium">Data</th>
                  <th className="text-left py-2 font-medium">Tipo</th>
                  <th className="text-left py-2 font-medium">Descrição</th>
                  <th className="text-right py-2 font-medium">Entrada</th>
                  <th className="text-right py-2 font-medium">Saída</th>
                </tr>
              </thead>
              <tbody>
                {data?.movements.slice(0, 20).map((mov) => (
                  <tr key={mov.id} className="border-b hover:bg-muted/50">
                    <td className="py-2">
                      {mov.date ? safeFormat(parseDate(mov.date)!, 'dd/MM', { locale: ptBR }) : '-'}
                    </td>
                    <td className="py-2">
                      <span className={`flex items-center gap-1 ${mov.type === 'entrada' ? 'text-emerald-600' : 'text-destructive'}`}>
                        {mov.type === 'entrada' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                        {mov.type === 'entrada' ? 'Entr' : 'Saída'}
                      </span>
                    </td>
                    <td className="py-2 truncate max-w-[200px]">{mov.description}</td>
                    <td className="py-2 text-right text-emerald-600">
                      {mov.type === 'entrada' && <MoneyDisplay value={mov.amount} showCents={false} />}
                    </td>
                    <td className="py-2 text-right text-destructive">
                      {mov.type === 'saida' && <MoneyDisplay value={mov.amount} showCents={false} />}
                    </td>
                  </tr>
                ))}
                {(!data?.movements || data.movements.length === 0) && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">
                      Nenhuma movimentação encontrada no período
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
